import type { VFrameBase } from '@festina/life-general-types/life'
import { useAuthenticationContext } from '@festina-life/authentication'
import {
  type ExcludeString,
  isEmpty,
  type Optional,
  typeUtils,
} from '@festina-life/core'
import { usePercentageFormatter, useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'
import * as Yup from 'yup'

import { isFieldRequired } from '../formUtility'
import { dateValidation } from './validation/dateValidation'
import { doubleValidation } from './validation/doubleValidation'
import { percentageValidation } from './validation/percentageValidation'
import {
  type AnyFrame,
  isBooleanVFrame,
  isDateVFrame,
  isDecimalVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
  isEnumVFrame,
  isFractionVFrame,
  isIntegerVFrame,
  isMoneyDecimalVFrame,
  isMoneyVFrame,
  isStringVFrame,
} from './validation/types'

type YupSchemaForType<T> = Required<{
  [K in keyof T]: T[K] extends string
    ?
        | Yup.StringSchema<
            Yup.Maybe<string | undefined>,
            Yup.AnyObject,
            undefined,
            ''
          >
        | Yup.ObjectSchema<Yup.AnyObject, Yup.AnyObject, unknown, ''>
        | Yup.AnySchema
    : T[K] extends number
      ?
          | Yup.NumberSchema<
              Yup.Maybe<number | undefined>,
              Yup.AnyObject,
              undefined,
              ''
            >
          | Yup.ObjectSchema<Yup.AnyObject, Yup.AnyObject, unknown, ''>
      : T[K] extends boolean
        ?
            | Yup.BooleanSchema<
                Yup.Maybe<boolean | undefined>,
                Yup.AnyObject,
                undefined,
                ''
              >
            | Yup.ObjectSchema<Yup.AnyObject, Yup.AnyObject, unknown, ''>
            | Yup.AnySchema
        :
            | Yup.ObjectSchema<Yup.AnyObject, Yup.AnyObject, unknown, ''>
            | Yup.AnySchema
}>

export const typeSafeYupObject = <T extends object>(
  obj: YupSchemaForType<T>,
): YupSchemaForType<T> => obj

type FrameSchema<T extends Optional<VFrameBase>> =
  ExcludeString<NonNullable<T>['_type']> extends ExcludeString<
    AnyFrame['_type']
  >
    ? Yup.AnySchema | undefined
    : Yup.ObjectSchema<Yup.AnyObject> | undefined

export const useCreateYupSchema = () => {
  const translate = useTranslate()
  const { isInTestMode } = useAuthenticationContext()
  const { formatPercentWithDecimals } = usePercentageFormatter()

  return useCallback(
    function createYupSchema<T extends Optional<VFrameBase>>(
      frame: T,
    ): FrameSchema<T> {
      if (!frame || isDontUseVFrame(frame)) {
        return undefined
      }

      let schema: Yup.AnySchema

      if (isStringVFrame(frame) || isEnumVFrame(frame)) {
        schema = Yup.string()
      } else if (isBooleanVFrame(frame)) {
        schema = Yup.boolean()
      } else if (isDateVFrame(frame)) {
        schema = dateValidation(frame, translate)
      } else if (isFractionVFrame(frame)) {
        schema = percentageValidation({
          frame,
          translate,
          formatPercentWithDecimals,
        })
      } else if (
        isIntegerVFrame(frame) ||
        isDecimalVFrame(frame) ||
        isMoneyDecimalVFrame(frame) ||
        isMoneyVFrame(frame)
      ) {
        schema = doubleValidation(frame, translate)
      } else {
        // Nested, defined inline as it's recursive.
        const objFrame: Partial<Record<keyof typeof frame, Yup.AnySchema>> = {}

        for (const key in frame) {
          if (key.startsWith('_')) {
            continue // _type
          }

          const frameKey = key as keyof typeof frame

          const frameField = typeUtils.hasField(frame, frameKey)
            ? frame[frameKey]
            : undefined

          if (isEmpty(frameField)) {
            continue
          } else if (typeof frameField === 'object') {
            if (Array.isArray(frameField)) {
              const nestedYupArray = createYupSchema(
                frameField[0] as typeof frame,
              )

              if (nestedYupArray) {
                // Some forms show both the initial and current values.
                // Deleting a nested object from an array would lead to
                // the "wrong" initial value being next to the currrent.
                // For example (represented as initial-current):
                //   Start  : A-1, B-2, C-3
                //   Delete : array item at position 1 (B-2)
                //   Now    : A-1, B-3, C-
                //            B is next to the 3 (wrong), and adding back
                //            a new item would match it to C (also wrong).
                //
                // For this reason, we leave empty holes in the array instead
                // of deleting the position and pushing all other items back.
                // Submit sanitization compacts these undefined rows after validation.
                objFrame[frameKey] = Yup.array().of(
                  nestedYupArray.optional().default(undefined),
                )
              }
            } else {
              const nestedYupObject = createYupSchema(frameField as VFrameBase)

              if (nestedYupObject) {
                objFrame[frameKey] = nestedYupObject
              }
            }
          } else if (isInTestMode && typeof frameKey === 'string') {
            throw new Error(
              `No Yup frame converter for key ${frameKey}: ${JSON.stringify(frame)}`,
            )
          }
        }

        return Yup.object(
          objFrame as Record<keyof T, Yup.AnySchema>,
        ) as Yup.ObjectSchema<Yup.AnyObject>
      }

      if (isFieldRequired(frame)) {
        schema = schema.required(translate('validation.required'))
      } else {
        schema = schema.nullable()
      }

      // We don't have an input for e.g. "number array" yet,
      // but string arrays have been observed (cf. LifeMultiCompanyIdInput)
      const supportsArrays = isEnumeratedVFrame(frame) || isStringVFrame(frame)

      if (supportsArrays && frame.isFramingListValue) {
        schema = isFieldRequired(frame)
          ? Yup.array(schema).required(translate('validation.required'))
          : Yup.array(schema)
      }

      return schema as FrameSchema<T>
    },
    [translate, isInTestMode, formatPercentWithDecimals],
  )
}
